import { getAPIErrorMessage } from '../../helpers/helpers';
import { PullDataService } from '../../service/PullDataService';
import { NotificationType } from '../../store/storeNotifications';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { checkExecuteTrigger } from '../../util/checkExecute';
import { MicrosoftService } from './PullMicrosoftData';

interface UseMicrosoftPullSubmitParams {
  data: any;
  formData: any;
  edges: any;
  setNodeState: any;
  nodeId: string;
  flowId: string;
  setSaving: any;
  setSchema: any;
  setIsLoading: (v: boolean) => void;
  setNotification: (v: any) => void;
  setHasToExecute: (v: boolean) => void;
}

export const useMicrosoftPullSubmit = ({
  data,
  formData,
  edges,
  setNodeState,
  nodeId,
  flowId,
  setSaving,
  setSchema,
  setIsLoading,
  setNotification,
  setHasToExecute,
}: UseMicrosoftPullSubmitParams) => {
  const saveResult = (text: string) => {
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id: nodeId,
      flowId,
      setSaving,
      flowTriggerData: checkExecuteTrigger(data, nodeId),
      changeType: 'execute',
      objectData: { text },
      setSchema,
    });
  };

  const onSubmitMicrosoftExcel = async () => {
    const fileId = data?.microsoftFileId;
    const worksheetId = data?.microsoftWorksheetId;

    if (!fileId) {
      return setNotification({
        type: NotificationType.Error,
        message: 'Please select an Excel file!',
      });
    }
    if (!worksheetId) {
      return setNotification({
        type: NotificationType.Error,
        message: 'Please select a worksheet!',
      });
    }

    let range = formData.rangeFilter ?? '';
    if (data['rangeFilterData']?.length) {
      range = data['rangeFilterData']
        .map((item: { text: string }) => item.text)
        .join('');
    }

    const response = await PullDataService.getMicrosoftExcelData({
      fileId,
      worksheetId,
      range: range || undefined,
    });

    saveResult(response.data.text);
  };

  const onSubmitMicrosoftWord = async () => {
    const fileId = data?.microsoftWordFileId;

    if (!fileId) {
      return setNotification({
        type: NotificationType.Error,
        message: 'Please select a Word document!',
      });
    }

    const contentType = formData.microsoftWordContentType ?? 'fullContent';

    let paragraphStart: number | undefined;
    let paragraphEnd: number | undefined;

    if (contentType === 'pullCert') {
      const startVal = formData.paragraphStart;
      const endVal = formData.paragraphEnd;

      if (
        !startVal ||
        isNaN(+startVal) ||
        +startVal < 0 ||
        !endVal ||
        isNaN(+endVal) ||
        +endVal < 0
      ) {
        return setNotification({
          type: NotificationType.Error,
          message:
            'Paragraph Start and Paragraph End are required as positive numbers!',
        });
      }

      paragraphStart = parseInt(startVal);
      paragraphEnd = parseInt(endVal);
    }

    const response = await PullDataService.getMicrosoftWordData({
      fileId,
      paragraphStart,
      paragraphEnd,
    });

    saveResult(response.data.text);
  };

  const onSubmitMicrosoftPowerPoint = async () => {
    const fileId = data?.microsoftPowerPointFileId;

    if (!fileId) {
      return setNotification({
        type: NotificationType.Error,
        message: 'Please select a PowerPoint file!',
      });
    }

    let slidesFilter = formData.slidesFilter ?? '';
    if (data['slidesFilterData']?.length) {
      slidesFilter = data['slidesFilterData']
        .map((item: { text: string }) => item.text)
        .join(',');
    }

    const response = await PullDataService.getMicrosoftPowerPointData({
      fileId,
      slidesFilter: slidesFilter || undefined,
    });

    saveResult(response.data.text);
  };

  const onSubmitMicrosoft = async () => {
    const service = data?.selectedMicrosoftService ?? MicrosoftService.Excel;

    setIsLoading(true);
    try {
      if (service === MicrosoftService.Word) {
        await onSubmitMicrosoftWord();
      } else if (service === MicrosoftService.PowerPoint) {
        await onSubmitMicrosoftPowerPoint();
      } else {
        await onSubmitMicrosoftExcel();
      }
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error),
      });
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        setHasToExecute(true);
      }, 2500);
    }
  };

  return { onSubmitMicrosoft };
};
